/**
 * Surface scale for the DFC Fitness Village experience (villages -> classes / courts).
 *
 * WHY THIS EXISTS, AND WHY IT IS NOT `DFC_NEUTRAL`
 * -----------------------------------------------
 * `dfcBrand.ts` owns the brand palette and `DFC_NEUTRAL` — the functional greys the
 * school-experience surfaces use (`#e5e7eb`, `#6b7280`). The village design uses a
 * different, fuller grey ramp: the Real Sports *Bookings* neutral scale. Rather than
 * redefine DFC_NEUTRAL under the school surfaces' feet, the village scale lives here.
 *
 * Consolidating the two ramps is worth doing and is deliberately NOT this slice's job.
 * See product/specs/ui/dfc-village-journeys.md, "Token reconciliation".
 *
 * Every value below is lifted from the Claude Design canvas
 * "Dubai Fitness Challenge booking theme" / DFC Journeys No Map.dc.html (read 2026-09-18).
 * Nothing here is invented; where the canvas used a raw literal, the literal is named here
 * once so no component ever writes a colour.
 */

import { DFC_PALETTE, withAlpha } from './dfcBrand';

/**
 * Bookings neutral ramp. The canvas wrote these as bare hex; they are the same values as
 * SKMDAdminPanel's `src/design-system/tokens.ts` neutral scale.
 */
const NEUTRAL = {
  n0: '#FFFFFF',
  n25: '#F8F9FB', // canvas / soft fill
  n50: '#F1F2F5', // segmented-control track
  n75: '#ECEEF2', // header hairline
  n100: '#DFE1E6', // default border
  n200: '#C1C7CF', // border, selected
  n300: '#A4ABB8', // muted text
  n500: '#666D80', // body text
  n700: '#272835', // headings, scrim
} as const;

/** Near-blacks the canvas used for image placeholders and the dark auth panel. */
const INK = {
  imagePlaceholder: '#111111',
  panel: '#0B0B0B',
} as const;

export type DfcThemeMode = 'light' | 'dark';

export interface DfcSurface {
  dark: boolean;
  /** Page background */
  pageBg: string;
  /** Card / row background */
  surface: string;
  /** Soft fill — notes, footers, stat panels */
  soft: string;
  /** Segmented-control and icon-button track */
  track: string;
  border: string;
  /** Border of an open / selected card — one step stronger than `border` */
  borderStrong: string;
  /** Sticky header background (translucent, blurred) */
  headerBg: string;
  headerBorder: string;
  /** Sticky action bar background */
  barBg: string;
  /** Primary text */
  ink: string;
  /** Secondary text */
  ink2: string;
  /** Muted text */
  ink3: string;
  /**
   * Accent for eyebrows and selected day labels.
   *
   * NOTE — in the canvas this is DFC blue in the light theme, which is 2.14:1 on white and
   * fails WCAG AA at every size. Per dfcBrand.ts's own contrast note, accent TEXT on a light
   * background is black here; blue survives as a non-text accent only. This is spec delta #1.
   */
  accentText: string;
  /** Non-text accent (rules, dots, bars) — safe to be blue/green */
  accentMark: string;
  /** Solid CTA */
  ctaBg: string;
  ctaFg: string;
  /** Sub-label inside a solid CTA */
  ctaSub: string;
  /** Arrow glyph inside a solid CTA */
  ctaArrow: string;
  /** "Selected" fill for chips, slots and tabs */
  onBg: string;
  onFg: string;
  /** Logo lockup plate */
  logoBg: string;
  logoPad: string;
  /** Scrim over a photographic card */
  scrim: string;
  imagePlaceholder: string;
  panel: string;
}

const LIGHT: DfcSurface = {
  dark: false,
  pageBg: NEUTRAL.n0,
  surface: NEUTRAL.n0,
  soft: NEUTRAL.n25,
  track: NEUTRAL.n50,
  border: NEUTRAL.n100,
  borderStrong: NEUTRAL.n200,
  headerBg: withAlpha(DFC_PALETTE.white, 0.94),
  headerBorder: NEUTRAL.n75,
  barBg: withAlpha(DFC_PALETTE.white, 0.95),
  ink: DFC_PALETTE.black,
  ink2: NEUTRAL.n500,
  ink3: NEUTRAL.n300,
  accentText: DFC_PALETTE.black,
  accentMark: DFC_PALETTE.blue,
  ctaBg: DFC_PALETTE.black,
  ctaFg: DFC_PALETTE.white,
  ctaSub: withAlpha(DFC_PALETTE.white, 0.72),
  ctaArrow: DFC_PALETTE.green,
  onBg: DFC_PALETTE.black,
  onFg: DFC_PALETTE.white,
  logoBg: 'transparent',
  logoPad: '0px',
  scrim: NEUTRAL.n700,
  imagePlaceholder: INK.imagePlaceholder,
  panel: DFC_PALETTE.white,
};

const DARK: DfcSurface = {
  dark: true,
  pageBg: DFC_PALETTE.black,
  surface: withAlpha(DFC_PALETTE.white, 0.05),
  soft: withAlpha(DFC_PALETTE.white, 0.06),
  track: withAlpha(DFC_PALETTE.white, 0.08),
  border: withAlpha(DFC_PALETTE.white, 0.16),
  borderStrong: withAlpha(DFC_PALETTE.white, 0.3),
  headerBg: withAlpha(DFC_PALETTE.black, 0.9),
  headerBorder: withAlpha(DFC_PALETTE.white, 0.12),
  barBg: withAlpha(DFC_PALETTE.black, 0.92),
  ink: DFC_PALETTE.white,
  ink2: withAlpha(DFC_PALETTE.white, 0.64),
  ink3: withAlpha(DFC_PALETTE.white, 0.48),
  accentText: DFC_PALETTE.green,
  accentMark: DFC_PALETTE.green,
  ctaBg: DFC_PALETTE.green,
  ctaFg: DFC_PALETTE.black,
  ctaSub: withAlpha(DFC_PALETTE.black, 0.7),
  ctaArrow: DFC_PALETTE.black,
  onBg: DFC_PALETTE.green,
  onFg: DFC_PALETTE.black,
  logoBg: DFC_PALETTE.white,
  logoPad: '4px 8px',
  scrim: DFC_PALETTE.black,
  imagePlaceholder: INK.imagePlaceholder,
  panel: INK.panel,
};

export const getDfcSurface = (mode: DfcThemeMode): DfcSurface => (mode === 'dark' ? DARK : LIGHT);

/** Bottom-up scrim over a photo card, so title text stays legible on any image. */
export const photoScrim = (surface: DfcSurface, from = 0.88, to = 0.12, stop = '62%'): string =>
  `linear-gradient(0deg, ${withAlpha(surface.scrim, from)}, ${withAlpha(surface.scrim, to)} ${stop})`;

/** Left-to-right scrim, used by the wide "currently selected" hero cards. */
export const photoScrimX = (surface: DfcSurface): string =>
  `linear-gradient(90deg, ${withAlpha(surface.scrim, 0.88)} 30%, ${withAlpha(surface.scrim, 0.25)})`;

export const DFC_SURFACE_NEUTRAL = NEUTRAL;

/**
 * Plates behind a village lockup. Each lockup is drawn on solid black or white, so the plate is
 * that exact colour and the artwork's edges disappear into it. Which plate shows follows the
 * lockup picked for the page mode (`getVillageLockup`) — normally the mode itself, but a
 * village missing one mode's artwork keeps the other's lockup on its own plate.
 */
export const DFC_LOCKUP_PLATE = {
  dark: { bg: DFC_PALETTE.black, border: withAlpha(DFC_PALETTE.white, 0.18) },
  light: { bg: NEUTRAL.n0 },
} as const;
